import { useState, useEffect, useCallback } from 'react'
import {
  typedCollection,
  onSnapshot,
  query,
  where,
  orderBy,
} from '@/lib/firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { adminMediaService, type MediaDocument } from '../services/admin.media.service'

interface UseAdminMediaOptions {
  folderId: string | null
}

export interface UploadBatch {
  total: number
  completed: number
  overallPct: number
}

export interface UseAdminMediaReturn {
  items: MediaDocument[]
  loading: boolean
  uploadProgress: UploadBatch | null
  uploadFiles: (files: File[]) => Promise<void>
  renameItem: (mediaId: string, name: string) => Promise<void>
  moveItem: (mediaId: string, folderId: string | null) => Promise<void>
  deleteItem: (mediaId: string) => Promise<void>
  saveAsCopy: (sourceItem: MediaDocument, blob: Blob) => Promise<void>
  overwriteWithCrop: (mediaId: string, blob: Blob) => Promise<void>
}

export function useAdminMedia({ folderId }: UseAdminMediaOptions): UseAdminMediaReturn {
  const { user } = useAuth()
  const [items, setItems] = useState<MediaDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [uploadProgress, setUploadProgress] = useState<UploadBatch | null>(null)

  useEffect(() => {
    setLoading(true)
    const col = typedCollection<Omit<MediaDocument, 'id'>>('media')
    const q = folderId === null
      ? query(col, orderBy('uploadedAt', 'desc'))
      : query(col, where('folderId', '==', folderId), orderBy('uploadedAt', 'desc'))

    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() } as MediaDocument)))
        setLoading(false)
      },
      (err) => {
        console.error('[useAdminMedia] Firestore query failed:', err)
        setLoading(false)
      }
    )

    return unsub
  }, [folderId])

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!user) return
      const total = files.length
      const perFilePct = new Array(total).fill(0)
      let completed = 0

      setUploadProgress({ total, completed, overallPct: 0 })

      try {
        await Promise.all(
          files.map((file, i) =>
            adminMediaService
              .uploadMedia(file, folderId, user.uid, (pct) => {
                perFilePct[i] = pct
                const overallPct = perFilePct.reduce((a, b) => a + b, 0) / total
                setUploadProgress({ total, completed, overallPct })
              })
              .then(() => {
                completed++
                perFilePct[i] = 100
                const overallPct = perFilePct.reduce((a, b) => a + b, 0) / total
                setUploadProgress({ total, completed, overallPct })
              })
          )
        )
      } finally {
        setUploadProgress(null)
      }
    },
    [user, folderId]
  )

  const renameItem = useCallback(
    (mediaId: string, name: string) => adminMediaService.renameMedia(mediaId, name),
    []
  )

  const moveItem = useCallback(
    (mediaId: string, dest: string | null) => adminMediaService.moveMedia(mediaId, dest),
    []
  )

  const deleteItem = useCallback(
    (mediaId: string) => adminMediaService.deleteMedia(mediaId),
    []
  )

  const saveAsCopy = useCallback(
    async (sourceItem: MediaDocument, blob: Blob) => {
      if (!user) return
      await adminMediaService.saveMediaAsCopy(sourceItem, blob, user.uid)
    },
    [user]
  )

  const overwriteWithCrop = useCallback(
    (mediaId: string, blob: Blob) => adminMediaService.overwriteMediaWithCrop(mediaId, blob),
    []
  )

  return { items, loading, uploadProgress, uploadFiles, renameItem, moveItem, deleteItem, saveAsCopy, overwriteWithCrop }
}
